import React from 'react';
import { useTranslation } from 'react-i18next';
import { BubbleMenu } from '@tiptap/react/menus';
import type { Editor } from '@tiptap/react';
import {
  ArrowUpToLine,
  ArrowDownToLine,
  ArrowLeftToLine,
  ArrowRightToLine,
  Rows3,
  Columns3,
  PanelTop,
  TableCellsMerge,
  TableCellsSplit,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

interface TableMenuProps {
  editor: Editor;
}

interface ActionProps {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  destructive?: boolean;
}

const Action: React.FC<ActionProps> = ({ title, icon: Icon, onClick, disabled, active, destructive }) => (
  <Button
    variant="ghost"
    size="sm"
    className={cn('h-7 w-7 p-0', destructive && 'text-destructive hover:text-destructive')}
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
  >
    <Icon className={cn('h-4 w-4', active && 'text-primary')} />
  </Button>
);

/**
 * Floating toolbar shown while the cursor is inside a table: row and column
 * insertion/removal, header row toggle, merge/split, and delete table.
 */
export const TableMenu: React.FC<TableMenuProps> = ({ editor }) => {
  const { t } = useTranslation();
  const chain = () => editor.chain().focus();
  const can = () => editor.can().chain().focus();

  return (
    <BubbleMenu
      className="bubble-menu"
      editor={editor}
      shouldShow={({ editor }) => editor.isActive('table')}
    >
      <div className="flex max-w-[calc(100vw-2rem)] flex-wrap items-center gap-0.5 rounded-lg border bg-background p-1 shadow-lg">
        <span className="px-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{t('editor.tableMenu.row')}</span>
        <Action title={t('editor.tableMenu.addRowAbove')} icon={ArrowUpToLine} onClick={() => chain().addRowBefore().run()} disabled={!can().addRowBefore().run()} />
        <Action title={t('editor.tableMenu.addRowBelow')} icon={ArrowDownToLine} onClick={() => chain().addRowAfter().run()} disabled={!can().addRowAfter().run()} />
        <Action title={t('editor.tableMenu.deleteRow')} icon={Rows3} onClick={() => chain().deleteRow().run()} disabled={!can().deleteRow().run()} destructive />

        <Separator orientation="vertical" className="mx-0.5 h-4" />

        <span className="px-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{t('editor.tableMenu.column')}</span>
        <Action title={t('editor.tableMenu.addColumnLeft')} icon={ArrowLeftToLine} onClick={() => chain().addColumnBefore().run()} disabled={!can().addColumnBefore().run()} />
        <Action title={t('editor.tableMenu.addColumnRight')} icon={ArrowRightToLine} onClick={() => chain().addColumnAfter().run()} disabled={!can().addColumnAfter().run()} />
        <Action title={t('editor.tableMenu.deleteColumn')} icon={Columns3} onClick={() => chain().deleteColumn().run()} disabled={!can().deleteColumn().run()} destructive />

        <Separator orientation="vertical" className="mx-0.5 h-4" />

        <Action title={t('editor.tableMenu.toggleHeaderRow')} icon={PanelTop} onClick={() => chain().toggleHeaderRow().run()} disabled={!can().toggleHeaderRow().run()} />
        <Action title={t('editor.tableMenu.mergeCells')} icon={TableCellsMerge} onClick={() => chain().mergeCells().run()} disabled={!can().mergeCells().run()} />
        <Action title={t('editor.tableMenu.splitCell')} icon={TableCellsSplit} onClick={() => chain().splitCell().run()} disabled={!can().splitCell().run()} />

        <Separator orientation="vertical" className="mx-0.5 h-4" />

        <Action title={t('editor.tableMenu.deleteTable')} icon={Trash2} onClick={() => chain().deleteTable().run()} destructive />
      </div>
    </BubbleMenu>
  );
};
